import React from "react";
import classNames from "../classNames";

const noop = () => {};
const DEFAULT_ICON = "bi-three-dots";

const defaultProps = {
  defaultValue: "",
  onChange: noop,
  options: [],
  placeholder: "Select…",
  icon: DEFAULT_ICON,
};

function mapOption(option) {
  if (typeof option === "string") {
    return { label: option, value: option };
  }
  return option;
}

function normalizeIconClass(icon) {
  if (!icon || typeof icon !== "string") return DEFAULT_ICON;
  const trimmed = icon.trim();
  return trimmed.startsWith("bi-") ? `bi ${trimmed}` : trimmed;
}

function InputEnumTooltip({
  className,
  options,
  defaultValue,
  onChange,
  placeholder,
  required,
  disabled,
  icon,
  ...rest
}) {
  const iconClassName = normalizeIconClass(icon);
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const searchInputRef = React.useRef(null);
  const [selected, setSelected] = React.useState(() => {
    const mapped = options.map(mapOption);
    const def = mapped.find(
      (o) => o.value === defaultValue || o.label === defaultValue,
    );
    return def || null;
  });
  const anchorRef = React.useRef(null);

  const mappedOptions = React.useMemo(() => options.map(mapOption), [options]);

  const filteredOptions = React.useMemo(() => {
    if (!search.trim()) return mappedOptions;
    const q = search.trim().toLowerCase();
    return mappedOptions.filter(
      (o) =>
        (o.label && String(o.label).toLowerCase().includes(q)) ||
        (o.value != null && String(o.value).toLowerCase().includes(q)),
    );
  }, [mappedOptions, search]);

  const handleClickOutside = React.useCallback((e) => {
    if (anchorRef.current && !anchorRef.current.contains(e.target)) {
      setOpen(false);
    }
  }, []);

  React.useEffect(() => {
    if (!open) return;
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, handleClickOutside]);

  React.useEffect(() => {
    if (open) {
      setSearch("");
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [open]);

  React.useEffect(() => {
    const def = mappedOptions.find(
      (o) => o.value === defaultValue || o.label === defaultValue,
    );
    setSelected(def || null);
  }, [defaultValue, mappedOptions]);

  function handleIconClick() {
    if (disabled) return;
    setOpen((prev) => !prev);
  }

  function handleSelect(option) {
    setSelected(option);
    onChange(option.value);
    setSearch("");
    setOpen(false);
  }

  function handleSearchChange(e) {
    setSearch(e.target.value);
  }

  const displayValue = selected ? selected.value : "";

  const arrowStyle = {
    position: "absolute",
    width: 0,
    height: 0,
    borderTop: "7px solid transparent",
    borderBottom: "7px solid transparent",
    borderRight: "7px solid var(--bs-border-color)",
    left: "-7px",
    top: "1rem",
    zIndex: 0,
  };
  const arrowInnerStyle = {
    position: "absolute",
    width: 0,
    height: 0,
    borderTop: "6px solid transparent",
    borderBottom: "6px solid transparent",
    borderRight: "6px solid var(--bs-body-bg)",
    left: "-6px",
    top: "1rem",
    zIndex: 1,
  };

  const handleInvalid = React.useCallback((e) => {
    if (anchorRef.current) {
      anchorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, []);

  return (
    <div
      ref={anchorRef}
      className={classNames("position-relative d-inline-flex", className)}
      style={{ width: "fit-content" }}
    >
      {required && (
        <input
          type="text"
          value={displayValue}
          required={required}
          tabIndex={-1}
          onChange={() => {}}
          onInvalid={handleInvalid}
          className="visually-hidden"
          aria-label={placeholder}
        />
      )}
      <button
        type="button"
        className={classNames(
          "btn btn-light border d-flex align-items-center justify-content-center rounded px-2",
          open && "border-primary text-primary",
        )}
        style={{ minWidth: "2rem", minHeight: "2rem" }}
        onClick={handleIconClick}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={placeholder}
      >
        <i className={iconClassName} aria-hidden />
      </button>

      {open && (
        <div
          className="d-flex align-items-flex-start"
          role="dialog"
          aria-label={placeholder}
          style={{
            position: "absolute",
            left: "100%",
            top: 0,
            marginLeft: "10px",
            zIndex: 1050,
          }}
        >
          <div style={arrowStyle} aria-hidden />
          <div style={arrowInnerStyle} aria-hidden />
          <div
            className="border rounded shadow-sm bg-white overflow-hidden d-flex flex-column"
            style={{ minWidth: "11rem", maxHeight: "280px" }}
          >
            <div className="input-group input-group-sm border-0 rounded-0 border-bottom bg-white">
              <span className="input-group-text border-0 bg-transparent rounded-0 text-secondary py-1 px-2">
                <i className="bi bi-search text-dark" aria-hidden />
              </span>
              <input
                ref={searchInputRef}
                type="text"
                className="form-control form-control-sm border-0 shadow-none bg-transparent"
                style={{ outline: "none" }}
                placeholder="Search…"
                value={search}
                onChange={handleSearchChange}
                onKeyDown={(e) => e.stopPropagation()}
                aria-label="Search options"
              />
            </div>
            {filteredOptions.length > 0 ? (
              <div
                className="list-group list-group-flush rounded-0 rounded-bottom overflow-auto"
                role="listbox"
                style={{ maxHeight: "240px" }}
              >
                {filteredOptions.map((option) => {
                  const isSelected = selected?.value === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={classNames(
                        "list-group-item list-group-item-action border-0 text-start",
                        isSelected && "active",
                      )}
                      onClick={() => handleSelect(option)}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-muted text-center small p-3 rounded-bottom">
                No matches
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

InputEnumTooltip.defaultProps = defaultProps;
export default InputEnumTooltip;
